import AttributesLoader from 'components/PageLayout/AttributesLoader';
import { findParentVpc } from 'components/ServersList/groupServersByParent';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useAssetList from 'modules/queries/asset/useList';
import { useLatestNotifications } from 'modules/queries/notification/useCurrent';
import { useParams } from 'react-router';
import { NUTANIX_PRIVATE_CLOUD_TYPES } from 'utility/nutanixProductCodes';
import AttributesContent from './AttributesContent';

interface AttributesProps {
	isLocationEnabled?: boolean;
	refetchInterval?: number;
	/** prefer displaying `ip` instead of `domain` */
	showIp?: boolean;
	/** override what would normally be displayed in the displayType attribute */
	displayType?: string;
	showStatusBadge?: boolean;
}

const Attributes = ({
	isLocationEnabled,
	refetchInterval,
	displayType: displayTypeProp,
	showIp,
	showStatusBadge = true,
}: AttributesProps) => {
	const { uniqId } = useParams<{ uniqId: string }>();
	const { data, isLoading, isError } = useAssetDetails(
		{
			alsowith: hostingDetailsAlsowith,
			uniq_id: uniqId,
		},
		{ enabled: Boolean(uniqId), refetchInterval },
	);

	// nothing in `asset/details` links a VM to its VPC, so the account's VPCs are
	// fetched to find it by region; a failure or a miss just leaves the link out
	const { data: vpcList } = useAssetList(
		{ type: NUTANIX_PRIVATE_CLOUD_TYPES, page_size: 9999 },
		{ enabled: Boolean(data?.isNutanixVm), keepPreviousData: true },
	);

	const { data: notifications, isLoading: isLoadingNotifications } =
		useLatestNotifications(
			{},
			{
				refetchInterval: 10000,
				enabled: Boolean(uniqId),
			},
		);

	if (isError) {
		return null;
	}

	if (isLoading || isLoadingNotifications) {
		return <AttributesLoader />;
	}

	const { displayType, ...rest } = data;
	const parentVpc = data.isNutanixVm
		? findParentVpc(data, vpcList?.items ?? [])
		: undefined;

	return (
		<AttributesContent
			showLocation={isLocationEnabled}
			notification={notifications?.[uniqId]}
			displayType={displayTypeProp || displayType}
			showIp={showIp}
			showStatusBadge={showStatusBadge}
			parentVpc={parentVpc}
			{...rest}
		/>
	);
};

export default Attributes;
